import {Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, TextField, Typography, useTheme} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {useState, useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {useFacility} from "../../hooks/useFacility";
import {useFacilityUsers} from "../../hooks/useFacilityUsers";
import {useFacilityUpdate} from "../../hooks/useFacilityUpdate";
import {useFacilityUsersDelete} from "../../hooks/useFacilityUsersDelete";
import {useFacilityUsersAdd} from "../../hooks/useFacilityUsersAdd";
import {useUserSearch} from "../../hooks/useUserSearch";
import {useBusinessUnitCodes} from "../../hooks/useBusinessUnitCodes";
import {useCountries} from "../../hooks/useCountries";
import {useStates} from "../../hooks/useStates";
import MyTextField from "../../components/MyTextField";
import MySelect from "../../components/MySelect";

const FacilityDetails = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const {facility, isLoading, isError, error, refetch} = useFacility(id);
    const {users, isLoading: isLoadingUsers, refetch: refetchUsers} = useFacilityUsers(id);
    const {mutate: updateFacility, isPending} = useFacilityUpdate();
    const {mutate: deleteUsers, isPending: isDeletingUsers} = useFacilityUsersDelete();
    const {mutate: addUsers, isPending: isAddingUsers} = useFacilityUsersAdd();
    const businessUnitCodes = useBusinessUnitCodes();
    
    const [isEditing, setIsEditing] = useState(false);
    const [editedValues, setEditedValues] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isEditingUsers, setIsEditingUsers] = useState(false);
    const [removedUsers, setRemovedUsers] = useState([]);
    const [displayedUsers, setDisplayedUsers] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showAddUsersModal, setShowAddUsersModal] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
    
    // Get the current businessUnitCode for countries lookup
    const currentBusinessUnitCode = isEditing 
        ? (editedValues.businessUnitCode ?? facility?.businessUnitCode)
        : facility?.businessUnitCode;
    
    const countries = useCountries(currentBusinessUnitCode, isEditing && !!currentBusinessUnitCode);
    
    // Get the current countryCode for states lookup
    const currentCountryCode = isEditing 
        ? (editedValues.country ?? facility?.country)
        : facility?.country;
    
    const states = useStates(currentBusinessUnitCode, currentCountryCode, isEditing && !!currentBusinessUnitCode && !!currentCountryCode);

    // Initialize displayedUsers when users data is loaded
    useEffect(() => {
        if (users) {
            setDisplayedUsers(users);
        }
    }, [users]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Clear Country field when businessUnitCode changes or Country is not in options
    useEffect(() => {
        if (isEditing) {
            const currentCountry = editedValues.country ?? facility?.country;
            
            // If there are countries and current country is not in the list, clear it
            if (countries.length > 0 && currentCountry) {
                const countryExists = countries.some(c => c.id === currentCountry);
                if (!countryExists) {
                    handleFieldChange("country", "");
                }
            }
        }
    }, [currentBusinessUnitCode, countries, isEditing]);

    // Clear State field when businessUnitCode or countryCode changes or State is not in options
    useEffect(() => {
        if (isEditing) {
            const currentState = editedValues.state ?? facility?.state;
            
            // If there are states and current state is not in the list, clear it
            if (states.length > 0 && currentState) {
                const stateExists = states.some(s => s.id === currentState);
                if (!stateExists) {
                    handleFieldChange("state", "");
                }
            }
        }
    }, [currentBusinessUnitCode, currentCountryCode, states, isEditing]);

    // Call user search API with debounced search string
    const {users: searchResults, isLoading: isSearching} = useUserSearch(
        0,
        5,
        debouncedSearch,
        facility?.businessUnitCode,
        showAddUsersModal && debouncedSearch.length > 0
    );

    const handleDeleteUser = (userId) => {
        const userToRemove = displayedUsers.find(u => u.id === userId);
        if (userToRemove) {
            setRemovedUsers(prev => [...prev, userToRemove]);
            setDisplayedUsers(prev => prev.filter(u => u.id !== userId));
        }
    };

    const handleAddUserSelection = (event, value) => {
        if (value && !selectedUsersToAdd.find(u => u.id === value.id)) {
            setSelectedUsersToAdd(prev => [...prev, value]);
            setSearchInput("");
        }
    };

    const handleRemoveSelectedUser = (userId) => {
        setSelectedUsersToAdd(prev => prev.filter(u => u.id !== userId));
    };

    const handleAddUsersCancel = () => {
        setShowAddUsersModal(false);
        setSearchInput("");
        setDebouncedSearch("");
        setSelectedUsersToAdd([]);
    };

    const handleAddUsersConfirm = () => {
        if (selectedUsersToAdd.length > 0) {
            // Add selected users to the top of the table
            setDisplayedUsers(prev => [...selectedUsersToAdd, ...prev]);
            
            // Close modal and reset
            setShowAddUsersModal(false);
            setSearchInput("");
            setDebouncedSearch("");
            setSelectedUsersToAdd([]);
        }
    };

    const userColumns = [
        ...(isEditingUsers ? [{
            field: "actions",
            headerName: "",
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: () => (
                <IconButton
                    size="small"
                    sx={{ color: "primary.main" }}
                    onClick={() => setShowAddUsersModal(true)}
                >
                    <AddIcon />
                </IconButton>
            ),
            renderCell: (params) => (
                <IconButton
                    size="small"
                    sx={{ color: "error.main" }}
                    onClick={() => handleDeleteUser(params.row.id)}
                >
                    <CloseIcon />
                </IconButton>
            ),
        }] : []),
        {field: "id", headerName: "ID", width: 100},
        {field: "businessUnitCode", headerName: "Business Unit", width: 150},
        {field: "login", headerName: "Login", width: 250},
        {field: "firstName", headerName: "First Name", width: 150},
        {field: "lastName", headerName: "Last Name", width: 150},
        {
            field: "activated",
            headerName: "Activated",
            width: 120,
            valueFormatter: (value) => value ? "Yes" : "No",
        },
        {
            field: "isFitter",
            headerName: "Fitter",
            width: 100,
            // Three states: the list read omits the flag — for every row — when it could not resolve
            // it, and a user added here but not yet saved has no flag either. Absent means "not
            // known", never "not a fitter", so it renders distinctly from No.
            valueFormatter: (value) => (value === true ? "Yes" : value === false ? "No" : "—"),
        },
    ];

    const handleEdit = () => {
        setIsEditing(true);
        setErrorMessage("");
    };

    const handleConfirm = () => {
        // Validate mandatory fields
        const name = editedValues.name ?? facility.name;
        const businessUnitCode = editedValues.businessUnitCode ?? facility.businessUnitCode;
        const country = editedValues.country ?? facility.country;

        const errors = [];
        if (!name || name.trim() === "") {
            errors.push("Name");
        }
        if (!businessUnitCode || businessUnitCode.trim() === "") {
            errors.push("Business Unit");
        }
        if (!country || country.trim() === "") {
            errors.push("Country");
        }

        if (errors.length > 0) {
            setErrorMessage(`The following fields are required: ${errors.join(", ")}`);
            return;
        }

        // Build payload with edited values or fallback to original facility values
        const payload = {
            id: facility.id,
            name: name,
            businessUnitCode: businessUnitCode,
            address1: editedValues.address1 ?? facility.address1,
            address2: editedValues.address2 ?? facility.address2,
            city: editedValues.city ?? facility.city,
            state: editedValues.state ?? facility.state,
            postalCode: editedValues.postalCode ?? facility.postalCode,
            country: country,
            bookable: facility.bookable ?? true,
        };

        updateFacility(payload, {
            onSuccess: (response) => {
                // The PUT call returns the updated facility data
                // Update the query cache with the response data instead of refetching
                queryClient.setQueryData(["facility", id], response.data);
                setIsEditing(false);
                setEditedValues({});
                setErrorMessage("");
            },
            onError: (error) => {
                console.error("Failed to update facility:", error);
                // Extract error message from response body or use default
                const status = error.response?.status;
                const message = error.response?.data?.message;
                
                if (message) {
                    setErrorMessage(message);
                } else if (status) {
                    setErrorMessage(`Failed to update facility. (status: ${status})`);
                } else {
                    setErrorMessage("Failed to update facility.");
                }
                
                // Keep isEditing true and editedValues intact so user can fix and retry
                // Don't call setIsEditing(false) or setEditedValues({})
            },
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedValues({});
        setErrorMessage("");
        refetch();
    };

    const handleFieldChange = (fieldName, value) => {
        setEditedValues(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    padding: 2,
                    backgroundColor: theme.palette.background.secondary,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Box sx={{position: "absolute", left: 16}}>
                    <Link
                        component="button"
                        variant="body1"
                        onClick={() => navigate("/facilities")}
                        sx={{cursor: "pointer"}}
                    >
                        Back to All Facilities
                    </Link>
                </Box>
                <Typography variant="h5">
                    Facility Details
                </Typography>
            </Box>
            <Box sx={{flex: 1, padding: 2, overflowX: "hidden", overflowY: "auto", backgroundColor: theme.palette.background.secondary}}>
                {isError ? (
                    <Typography color="error">
                        Error loading facility: {error?.message || "Unknown error"}
                    </Typography>
                ) : (
                    <Box sx={{ maxWidth: "100%", width: "100%" }}>
                        <Card
                            sx={{
                                marginBottom: 3,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                borderRadius: 2,
                                width: "100%",
                            }}
                        >
                            <CardContent sx={{ padding: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 3,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.primary",
                                            borderBottom: "2px solid",
                                            borderColor: "primary.main",
                                            paddingBottom: 1,
                                            display: "inline-block",
                                        }}
                                    >
                                        Basic Info
                                    </Typography>
                                    <Box>
                                        {isPending ? (
                                            <CircularProgress size={24} />
                                        ) : !isEditing ? (
                                            <IconButton
                                                onClick={handleEdit}
                                                sx={{
                                                    color: "primary.main",
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="text"
                                                    onClick={handleCancel}
                                                    sx={{
                                                        marginRight: 1,
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleConfirm}
                                                >
                                                    Save
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                {errorMessage && (
                                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                                        {errorMessage}
                                    </Alert>
                                )}
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12}>
                                        <MyTextField
                                            label="Name *"
                                            value={isEditing ? (editedValues.name ?? facility?.name ?? "") : (facility?.name || "")}
                                            editable={isEditing && !isPending}
                                            fullWidth
                                            onChange={(e) => handleFieldChange("name", e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {isEditing ? (
                                            <MySelect
                                                label="Business Unit *"
                                                options={businessUnitCodes}
                                                value={editedValues.businessUnitCode ?? facility?.businessUnitCode ?? ""}
                                                editable={!isPending}
                                                onChange={(value) => handleFieldChange("businessUnitCode", value)}
                                            />
                                        ) : (
                                            <MyTextField
                                                label="Business Unit *"
                                                value={facility?.businessUnitCode || ""}
                                                editable={false}
                                                fullWidth
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {isEditing ? (
                                            <MySelect
                                                label="Country *"
                                                options={countries}
                                                value={editedValues.country ?? facility?.country ?? ""}
                                                editable={!isPending}
                                                onChange={(value) => handleFieldChange("country", value)}
                                            />
                                        ) : (
                                            <MyTextField
                                                label="Country *"
                                                value={facility?.country || ""}
                                                editable={false}
                                                fullWidth
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MyTextField
                                            label="Address 1"
                                            value={isEditing ? (editedValues.address1 ?? facility?.address1 ?? "") : (facility?.address1 || "")}
                                            editable={isEditing && !isPending}
                                            fullWidth
                                            onChange={(e) => handleFieldChange("address1", e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MyTextField
                                            label="Address 2"
                                            value={isEditing ? (editedValues.address2 ?? facility?.address2 ?? "") : (facility?.address2 || "")}
                                            editable={isEditing && !isPending}
                                            fullWidth
                                            onChange={(e) => handleFieldChange("address2", e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MyTextField
                                            label="City"
                                            value={isEditing ? (editedValues.city ?? facility?.city ?? "") : (facility?.city || "")}
                                            editable={isEditing && !isPending}
                                            fullWidth
                                            onChange={(e) => handleFieldChange("city", e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {isEditing ? (
                                            <MySelect
                                                label="State"
                                                options={states}
                                                value={editedValues.state ?? facility?.state ?? ""}
                                                editable={!isPending}
                                                onChange={(value) => handleFieldChange("state", value)}
                                            />
                                        ) : (
                                            <MyTextField
                                                label="State"
                                                value={facility?.state || ""}
                                                editable={false}
                                                fullWidth
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MyTextField
                                            label="Postal Code"
                                            value={isEditing ? (editedValues.postalCode ?? facility?.postalCode ?? "") : (facility?.postalCode || "")}
                                            editable={isEditing && !isPending}
                                            fullWidth
                                            onChange={(e) => handleFieldChange("postalCode", e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card
                            sx={{
                                marginBottom: 3,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                borderRadius: 2,
                                width: "100%",
                            }}
                        >
                            <CardContent sx={{ padding: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.primary",
                                            borderBottom: "2px solid",
                                            borderColor: "primary.main",
                                            paddingBottom: 1,
                                            display: "inline-block",
                                        }}
                                    >
                                        Users
                                    </Typography>
                                    <Box>
                                        {!isEditingUsers ? (
                                            <IconButton
                                                onClick={() => setIsEditingUsers(true)}
                                                sx={{
                                                    color: "primary.main",
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="text"
                                                    onClick={() => {
                                                        setIsEditingUsers(false);
                                                        setRemovedUsers([]);
                                                        setDisplayedUsers(users);
                                                    }}
                                                    sx={{
                                                        marginRight: 1,
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        // Calculate users to add and remove
                                                        const originalUserIds = users.map(u => u.id);
                                                        const displayedUserIds = displayedUsers.map(u => u.id);
                                                        
                                                        const usersToAdd = displayedUsers.filter(u => !originalUserIds.includes(u.id));
                                                        const usersToRemove = users.filter(u => !displayedUserIds.includes(u.id));
                                                        
                                                        if (usersToAdd.length > 0 || usersToRemove.length > 0) {
                                                            setRemovedUsers(usersToRemove);
                                                            setShowConfirmModal(true);
                                                        } else {
                                                            setIsEditingUsers(false);
                                                        }
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ marginTop: 2, width: "100%" }}>
                                    {isLoadingUsers ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minHeight: 200,
                                            }}
                                        >
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <DataGrid
                                            rows={displayedUsers || []}
                                            columns={userColumns}
                                            density="compact"
                                            disableRowSelectionOnClick
                                            hideFooter
                                            getRowClassName={(params) => {
                                                const originalUserIds = users?.map(u => u.id) || [];
                                                return !originalUserIds.includes(params.row.id) ? "newly-added-row" : "";
                                            }}
                                            sx={{
                                                "& .newly-added-row": {
                                                    backgroundColor: "#f5f5dc",
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
            <Dialog open={showConfirmModal}>
                <DialogTitle>Confirm Changes</DialogTitle>
                <DialogContent>
                    {(() => {
                        // Calculate users to add and remove
                        const originalUserIds = users?.map(u => u.id) || [];
                        const displayedUserIds = displayedUsers?.map(u => u.id) || [];
                        
                        const usersToRemove = users?.filter(u => !displayedUserIds.includes(u.id)) || [];
                        const usersToAdd = displayedUsers?.filter(u => !originalUserIds.includes(u.id)) || [];
                        
                        return (
                            <>
                                {usersToRemove.length > 0 && (
                                    <Box sx={{ marginBottom: usersToAdd.length > 0 ? 2 : 0 }}>
                                        <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                            Removing users:
                                        </Typography>
                                        <Box component="ul" sx={{ marginTop: 1, paddingLeft: 2 }}>
                                            {usersToRemove.map((user) => (
                                                <li key={user.id}>
                                                    <Typography variant="body2">
                                                        {user.firstName} {user.lastName} ({user.login})
                                                    </Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {usersToAdd.length > 0 && (
                                    <Box>
                                        <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                            Adding users:
                                        </Typography>
                                        <Box component="ul" sx={{ marginTop: 1, paddingLeft: 2 }}>
                                            {usersToAdd.map((user) => (
                                                <li key={user.id}>
                                                    <Typography variant="body2">
                                                        {user.firstName} {user.lastName} ({user.login})
                                                    </Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    {(isDeletingUsers || isAddingUsers) ? (
                        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", padding: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <>
                            <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    // Calculate users to add and remove
                                    const originalUserIds = users?.map(u => u.id) || [];
                                    const displayedUserIds = displayedUsers?.map(u => u.id) || [];
                                    
                                    const usersToRemove = users?.filter(u => !displayedUserIds.includes(u.id)) || [];
                                    const usersToAdd = displayedUsers?.filter(u => !originalUserIds.includes(u.id)) || [];
                                    
                                    const userIdsToRemove = usersToRemove.map(u => u.id);
                                    const userIdsToAdd = usersToAdd.map(u => u.id);
                                    
                                    // Function to handle completion
                                    const handleComplete = () => {
                                        setShowConfirmModal(false);
                                        setIsEditingUsers(false);
                                        setRemovedUsers([]);
                                        refetchUsers();
                                    };
                                    
                                    // Function to handle errors
                                    const handleError = (error, operation) => {
                                        console.error(`Failed to ${operation} users:`, error);
                                        // TODO: Show error message to user
                                    };
                                    
                                    // If both operations needed, do delete first then add
                                    if (userIdsToRemove.length > 0 && userIdsToAdd.length > 0) {
                                        deleteUsers(
                                            { facilityId: id, userIds: userIdsToRemove },
                                            {
                                                onSuccess: () => {
                                                    // After delete succeeds, add users
                                                    addUsers(
                                                        { facilityId: id, userIds: userIdsToAdd },
                                                        {
                                                            onSuccess: handleComplete,
                                                            onError: (error) => handleError(error, "add"),
                                                        }
                                                    );
                                                },
                                                onError: (error) => handleError(error, "delete"),
                                            }
                                        );
                                    } else if (userIdsToRemove.length > 0) {
                                        // Only delete
                                        deleteUsers(
                                            { facilityId: id, userIds: userIdsToRemove },
                                            {
                                                onSuccess: handleComplete,
                                                onError: (error) => handleError(error, "delete"),
                                            }
                                        );
                                    } else if (userIdsToAdd.length > 0) {
                                        // Only add
                                        addUsers(
                                            { facilityId: id, userIds: userIdsToAdd },
                                            {
                                                onSuccess: handleComplete,
                                                onError: (error) => handleError(error, "add"),
                                            }
                                        );
                                    }
                                }}
                            >
                                Confirm
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={showAddUsersModal} maxWidth="sm" fullWidth>
                <DialogTitle>Search and Select Users</DialogTitle>
                <DialogContent>
                    <Box sx={{ marginTop: 2 }}>
                        <Autocomplete
                            options={searchResults || []}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.login})`}
                            loading={isSearching}
                            inputValue={searchInput}
                            onInputChange={(event, newValue) => {
                                if (event && event.type !== 'blur') {
                                    setSearchInput(newValue);
                                }
                            }}
                            onChange={handleAddUserSelection}
                            ListboxProps={{
                                sx: { maxHeight: '500px' }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Enter name, login, or email"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText={searchInput.length > 0 ? "No users found" : "Start typing to search"}
                        />
                    </Box>
                    {selectedUsersToAdd.length > 0 && (
                        <Box sx={{ marginTop: 3 }}>
                            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                                Selected Users:
                            </Typography>
                            <List dense>
                                {selectedUsersToAdd.map((user) => (
                                    <ListItem
                                        key={user.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleRemoveSelectedUser(user.id)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <DeleteIcon sx={{ visibility: "hidden" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={`${user.firstName} ${user.lastName} (${user.login})`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddUsersCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddUsersConfirm}
                        disabled={selectedUsersToAdd.length === 0}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FacilityDetails;

